import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import { useSafetySoundClusters } from '../useSafetyPageData';

/**
 * ### SoundSection
 *
 * The detected sounds offered in the page language as the same badge row the Industrial page
 * uses, one row per situation group. The group name and its one-line
 * meaning sit in the left column, the badges in the right, divided like the
 * monitoring list below so the two pages read as one family. Every badge is
 * in the server-rendered HTML; nothing is revealed on hover.
 *
 * @returns The rendered sound section element
 * @see {@link useSafetySoundClusters} The groups rendered here
 */
export default function SoundSection() {
  const { t } = useTranslation('common');
  const lang = useCurrentLang();
  const clusters = useSafetySoundClusters(t, lang);

  return (
    <section data-analytics-section="recognized_sounds">
      <Area>
        <SectionTitle
          color="lighter"
          title={t('Listen AI 인식 사운드')}
          description={t(
            '현장에서 나는 소리를 다섯 가지 상황으로 나누어 감지해요.',
          )}
        />
        <AniSlideUp>
          <ul className="mt-[16px] lg:mt-[clamp(16px,2.5vw,36px)]">
            {clusters.map((cluster) => (
              <li
                key={cluster.id}
                className="py-[24px] not-last:border-b not-last:border-b-apps-brandGray50 lg:grid lg:grid-cols-[calc(484/1480*100%)_calc(976/1480*100%)] lg:gap-[20px] lg:py-[clamp(24px,3.333vw,40px)]"
              >
                <div>
                  <h3 className="text-[22px] font-bold leading-[160%] tracking-[-0.44px] lg:text-[clamp(22px,2.333vw,28px)] lg:leading-[140%] lg:tracking-[-0.56px]">
                    {cluster.title}
                  </h3>
                  <p className="mt-[4px] text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray600 lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                    {cluster.description}
                  </p>
                </div>
                <ul className="mt-[16px] flex flex-wrap gap-3 lg:mt-0 lg:gap-4">
                  {cluster.sounds.map((sound) => (
                    <li key={sound}>
                      <span className="inline-flex min-h-[45px] max-w-full items-center whitespace-nowrap rounded-[48px] bg-apps-coreGray01 px-5 py-0 text-[18px] font-medium leading-[1.4] text-apps-coreGray06 lg:min-h-[56px] lg:px-7 lg:text-[20px]">
                        {sound}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </AniSlideUp>
      </Area>
    </section>
  );
}
